import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import Box from '../../components/Common/Box';
import Loader from '../../components/Common/Loader';
import { partnerService, PartnerStats } from '../../services/partnerService';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { useScreenType } from '../../hooks/useScreenType';

const PartnerSettings: React.FC = () => {
  const { isMobile } = useScreenType();
  const [stats, setStats] = useState<PartnerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await partnerService.getStats();
        setStats(data);
      } catch {
        setError('Impossible de charger les données partenaire.');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return <Loader />;
  if (error) return <div className='error-message'>{error}</div>;
  if (!stats || !stats.is_partner) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <SiteLayout>
      <Header icon='handshake' title='Espace Partenaire' />
      <div style={{ padding: isMobile ? 0 : 20, maxWidth: 1600, margin: '0 auto' }}>
        <div className='responsive-grid' style={{ gap: '20px', marginBottom: '30px' }}>
          <Box>
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Étudiants parrainés</p>
              <h3 style={{ fontSize: '32px', color: 'var(--text-heading)' }}>{stats.summary.total_students}</h3>
            </div>
          </Box>
          <Box>
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Commissions en attente</p>
              <h3 style={{ fontSize: '32px', color: 'var(--text-heading)' }}>
                {stats.summary.pending_commission.toLocaleString()} {stats.summary.currency}
              </h3>
            </div>
          </Box>
          <Box>
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Commissions payées</p>
              <h3 style={{ fontSize: '32px', color: 'var(--text-heading)' }}>
                {stats.summary.paid_commission.toLocaleString()} {stats.summary.currency}
              </h3>
            </div>
          </Box>
        </div>

        <Box>
          <div className='box-title box-vertical-padding box-horizontal-padding'>
            <p style={{ color: 'var(--text-heading)' }}>Mes Codes Parrainage</p>
          </div>
          <div style={{ padding: '20px' }}>
            <p style={{ marginBottom: '15px', fontSize: '14px', color: 'var(--text-main)' }}>
              Partagez ces codes avec vos étudiants. Ils recevront une réduction et vous cumulerez des
              commissions.
            </p>
            <table className='data-table' style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th className='left' style={{ color: 'var(--text-heading)' }}>Code</th>
                  <th className='center' style={{ color: 'var(--text-heading)' }}>Réduction Étudiant</th>
                  <th className='center' style={{ color: 'var(--text-heading)' }}>Ma Commission</th>
                </tr>
              </thead>
              <tbody>
                {stats.codes.map((c, i) => (
                  <tr key={i}>
                    <td className='left'>
                      <strong
                        style={{
                          background: 'var(--bg-hover)',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px dashed var(--border-color)',
                          color: 'var(--text-heading)'
                        }}
                      >
                        {c.code}
                      </strong>
                    </td>
                    <td className='center' style={{ color: 'var(--text-main)' }}>{ c.discount > 1 ? c.discount : (c.discount * 100).toFixed(0) }%</td>
                    <td className='center' style={{ color: 'var(--text-main)' }}>{ c.commission > 1 ? c.commission : (c.commission * 100).toFixed(0) }%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Box>

        <div style={{ marginTop: '30px' }}>
          <Box>
            <div className='box-title box-vertical-padding box-horizontal-padding'>
              <p style={{ color: 'var(--text-heading)' }}>Historique des parrainages</p>
            </div>
            <div style={{ padding: '20px' }}>
              {stats.history.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  Aucun parrainage enregistré pour le moment.
                </p>
              ) : (
                <table className='data-table' style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th className='left' style={{ color: 'var(--text-heading)' }}>Date</th>
                      <th className='left' style={{ color: 'var(--text-heading)' }}>Utilisateur</th>
                      <th className='right' style={{ color: 'var(--text-heading)' }}>Commission</th>
                      <th className='center' style={{ color: 'var(--text-heading)' }}>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.history.map((h) => (
                      <tr key={h.id}>
                        <td className='left' style={{ color: 'var(--text-main)' }}>{new Date(h.date).toLocaleDateString()}</td>
                        <td className='left' style={{ color: 'var(--text-main)' }}>{h.user_name}</td>
                        <td className='right' style={{ color: 'var(--text-main)' }}>
                          {h.amount.toLocaleString()} {stats.summary.currency}
                        </td>
                        <td className='center'>
                          <span
                            className={`status-badge ${h.status.toLowerCase()}`}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              background: h.status === 'PAID' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                              color: h.status === 'PAID' ? '#10b981' : '#f59e0b',
                            }}
                          >
                            {h.status === 'PAID' ? 'PAYÉ' : 'EN ATTENTE'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Box>
        </div>
      </div>
    </SiteLayout>
  );
};

export default PartnerSettings;
